import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './DowngradeConfirmation.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ArrowDownSquareContained, ArrowRightSquareContained, CheckBroken } from 'blaze-ui/icons'
import XSquareContained from 'blaze-ui/icons/24/XSquareContained'
import { FREE_REWARD_TIERS } from 'hooks/useAutopilotLimits'
import dayjs from 'dayjs'

const FromGrowth = ({ isTrialing }: { isTrialing: boolean }) => {
  return (
    <>
      <div className={Styles.header}>
        <Text variant="label">Your current plan</Text>
        <Heading level={4}>Growth</Heading>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Parallel campaigns</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          Crossposting
        </Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Multi-user workflow (reviews & approvals)</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">AI-powered ad management</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited channels</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited seats</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">
          {isTrialing ? '500' : '2000'} content credits {isTrialing ? 'during trial' : 'per month'}
        </Text>
      </div>
    </>
  )
}

const FromPioneer = () => {
  return (
    <>
      <div className={Styles.header}>
        <Text variant="label">Your current plan</Text>
        <Heading level={4}>Pioneer</Heading>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Parallel campaigns</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          Crossposting
        </Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Multi-user workflow (reviews & approvals)</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">AI-powered ad management</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited channels</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited seats</Text>
      </div>
    </>
  )
}

const FromStarter = () => {
  return (
    <>
      <div className={Styles.header}>
        <Text variant="label">Your current plan</Text>
        <Heading level={4}>Starter</Heading>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">No Blaze watermark</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          3 channels
        </Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited posts per week per channel</Text>
      </div>
    </>
  )
}

const ToStarter = ({ isTrialing }: { isTrialing: boolean }) => {
  return (
    <>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <div className={Styles.multiLineText}>
          <Text>
            <strong>No</strong> strategic multi-week campaigns – you’ll get simple weekly batches instead.
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <Text fontWeight="700">No</Text>
        <Text>crossposting</Text>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          No
        </Text>
        <Text>multi-user workflow (reviews & approvals)</Text>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          No
        </Text>
        <Text>AI-powered ad management</Text>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <Text fontWeight="700">3</Text>
        <Text>channels</Text>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <Text fontWeight="700">1</Text>
        <Text>seat</Text>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <Text fontWeight="700">{isTrialing ? '200' : '800'}</Text>
        <Text>content credits {isTrialing ? 'during trial' : 'per month'}</Text>
      </div>
    </>
  )
}

const ToFree = ({ fromPlan }: { fromPlan: string }) => {
  if (fromPlan === 'Starter') {
    return (
      <>
        <div className={Styles.detail}>
          <ArrowDownSquareContained color="var(--orange-50)" size={22} />
          <Text fontWeight="700">Blaze watermark</Text>
          <Text>on all content</Text>
        </div>
        <div className={Styles.detail}>
          <ArrowRightSquareContained color="var(--dark-40)" size={22} />
          <Text fontWeight="700">3</Text>
          <Text>Channels</Text>
        </div>
        <div className={Styles.detail}>
          <ArrowDownSquareContained color="var(--orange-50)" size={22} />
          <Text fontWeight="700" lineClamp={1}>
            1
          </Text>
          <Text>Post per week per channel</Text>
        </div>
      </>
    )
  }

  return (
    <>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <div className={Styles.multiLineText}>
          <Text>
            <strong>No</strong> strategic multi-week campaigns – you’ll get simple weekly batches instead.
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <Text fontWeight="700">No</Text>
        <Text>crossposting</Text>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          No
        </Text>
        <Text>multi-user workflow (reviews & approvals)</Text>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          No
        </Text>
        <Text>AI-powered ad management</Text>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          Blaze watermark
        </Text>
        <Text>on all content</Text>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <Text fontWeight="700">3</Text>
        <Text>channels</Text>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <Text fontWeight="700">1</Text>
        <Text>seat</Text>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <Text fontWeight="700">{FREE_REWARD_TIERS[0]}</Text>
        <Text>post per week per channel</Text>
      </div>
    </>
  )
}

export const DowngradeConfirmation = ({
  isStarterPlan,
  currentPlanName,
  onBack,
  onConfirm,
  trialDaysLeft,
}: {
  isStarterPlan: boolean
  currentPlanName: string
  onBack: () => void
  onConfirm: () => void
  trialDaysLeft: number
}) => {
  const { closeModal } = useModals()
  const toPlanName = isStarterPlan ? 'Starter' : 'Free'
  const fromGrowthToStarter = currentPlanName === 'Growth' && toPlanName === 'Starter'
  const fromGrowthToFree = currentPlanName === 'Growth' && toPlanName === 'Free'
  const isTrialing = trialDaysLeft > 0

  return (
    <>
      <Modal.Header>
        <div className={Styles.header}>
          <Heading level={3}>{`You're downgrading to the ${toPlanName} ${isTrialing ? 'trial' : 'plan'}`}</Heading>
          {isTrialing && fromGrowthToStarter && (
            <Text variant="secondary">
              Your Growth trial will switch to a Starter trial right away and will end on{' '}
              {dayjs().add(trialDaysLeft, 'day').format('MMM D, YYYY')}.
            </Text>
          )}
          {!isTrialing && fromGrowthToStarter && (
            <Text variant="secondary">
              Your Starter plan starts right away. The prorated balance from your Growth plan will be applied to your
              next invoice.
            </Text>
          )}
          {!isTrialing && fromGrowthToFree && <Text variant="secondary">Upgrade anytime to regain full access.</Text>}
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className={Styles.content}>
          <div className={cx(Styles.details, Styles.left)}>
            {currentPlanName === 'Growth' && <FromGrowth isTrialing={isTrialing} />}
            {currentPlanName === 'Starter' && <FromStarter />}
            {currentPlanName === 'Pioneer' && <FromPioneer />}
          </div>
          <div className={cx(Styles.details, Styles.right)}>
            <div className={Styles.header}>
              <Text variant="label">Your new plan</Text>
              <Heading level={4}>{toPlanName}</Heading>
            </div>
            {isStarterPlan ? <ToStarter isTrialing={isTrialing} /> : <ToFree fromPlan={currentPlanName} />}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={onBack}>
            Back
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right" className={Styles.footerRight}>
          <Modal.FooterButton variant="tertiary" onPress={closeModal}>
            Stay on {currentPlanName}
          </Modal.FooterButton>
          <Modal.FooterButton onPress={onConfirm}>Continue</Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
